import { z } from 'zod';

import type { Fetcher } from '@/lib/api/fetcher';

import type {
  ClientProfile,
  CommissionPreview,
  CommissionRule,
  NoteKind,
  ServerTime,
  SessionDetail,
  SessionEvent,
  SessionPage,
  SessionStatus,
  StaffRole,
  Statement,
  StepType,
} from './schemas';
import {
  clientProfileSchema,
  commissionPreviewSchema,
  commissionRuleSchema,
  eventSchema,
  sessionDetailSchema,
  sessionPageSchema,
  statementSchema,
  timeSchema,
} from './schemas';

/**
 * One function per BP-05 §5 route the §6 screens use, over the shared transport
 * (`lib/api/fetcher`).
 *
 * Every write takes an `Idempotency-Key` from the caller rather than minting one here
 * (ARCHITECTURE §3 API): a retry has to send the **same** key, and only the screen that
 * owns the button knows whether this click is the first or the retry. The timer screen
 * leans on that hard — a technician on a 300 ms link taps "Top coat" twice.
 */

/** `POST /sessions` body (§5 `SessionAdhocIn`) — the walk-in with no booking. */
export interface AdhocInput {
  client_id?: string | null;
  service_id: string;
  addon_ids?: string[];
  staff_id: string;
  station_id?: string | null;
}

/** `POST /sessions/{id}/steps` body (§5 `StepStartIn`). */
export interface StepInput {
  type: StepType;
  label?: string | null;
  staff_id?: string | null;
}

/** `POST /sessions/{id}/handover` body (§5 `HandoverIn`, R6). */
export interface HandoverInput {
  to_staff_id: string;
  role?: StaffRole;
  reason?: string | null;
}

/** `POST /sessions/{id}/materials` body (§5 `MaterialIn`, R7). */
export interface MaterialInput {
  sku_id: string;
  qty: string;
  unit: string;
  step_type?: StepType | null;
  sell?: boolean;
}

/** `PATCH /sessions/{id}/materials/{materialId}` body (§5 `MaterialPatchIn`). */
export interface MaterialPatch {
  qty: string;
  step_type?: StepType | null;
}

/** `POST /sessions/{id}/abandon` body (§5 `AbandonIn`, §14 #4). */
export interface AbandonInput {
  reason: string;
  consume?: boolean;
  frontdesk_confirmed?: boolean;
}

/** `POST`/`PATCH /commission-rules` body (§5 `CommissionRuleIn`, R15–R17). */
export interface CommissionRuleInput {
  name: string;
  scope: CommissionRule['scope'];
  scope_id?: string | null;
  applies_to: CommissionRule['applies_to'];
  staff_id?: string | null;
  role?: string | null;
  basis: CommissionRule['basis'];
  percent_bp?: number | null;
  fixed_paise?: number | null;
  tiers?: { from_paise: number; percent_bp: number }[] | null;
  split_mode: CommissionRule['split_mode'];
  primary_share_bp?: number | null;
  priority?: number;
  valid_from: string;
  valid_to?: string | null;
  active?: boolean;
}

/** `GET /sessions` filters (§5) — ANDed, all optional. */
export interface SessionQuery {
  station_id?: string;
  status?: SessionStatus;
  date?: string;
}

/** Every §5 call BP-05's screens make. */
export interface SessionsApi {
  readTime: () => Promise<ServerTime>;
  listSessions: (query?: SessionQuery) => Promise<SessionPage>;
  readSession: (id: string) => Promise<SessionDetail>;
  createSession: (body: AdhocInput, idempotencyKey: string) => Promise<SessionDetail>;
  startStep: (id: string, body: StepInput, idempotencyKey: string) => Promise<SessionDetail>;
  stopStep: (id: string, idempotencyKey: string) => Promise<SessionDetail>;
  pause: (id: string, idempotencyKey: string) => Promise<SessionDetail>;
  resume: (id: string, idempotencyKey: string) => Promise<SessionDetail>;
  handover: (id: string, body: HandoverInput, idempotencyKey: string) => Promise<SessionDetail>;
  addMaterial: (id: string, body: MaterialInput, idempotencyKey: string) => Promise<SessionDetail>;
  changeMaterial: (id: string, materialId: string, body: MaterialPatch) => Promise<SessionDetail>;
  removeMaterial: (id: string, materialId: string) => Promise<SessionDetail>;
  repeatLast: (id: string, idempotencyKey: string) => Promise<SessionDetail>;
  addNote: (
    id: string,
    body: { body: string; kind?: NoteKind },
    idempotencyKey: string,
  ) => Promise<SessionDetail>;
  readClientProfile: (id: string) => Promise<ClientProfile>;
  complete: (
    id: string,
    confirmMaterials: boolean,
    idempotencyKey: string,
  ) => Promise<SessionDetail>;
  abandon: (id: string, body: AbandonInput, idempotencyKey: string) => Promise<SessionDetail>;
  readEvents: (id: string) => Promise<SessionEvent[]>;
  listRules: () => Promise<CommissionRule[]>;
  createRule: (body: CommissionRuleInput, idempotencyKey: string) => Promise<CommissionRule>;
  updateRule: (id: string, body: CommissionRuleInput) => Promise<CommissionRule>;
  previewRules: (invoiceId: string) => Promise<CommissionPreview[]>;
  readStatement: (query: { period: string; staff_id?: string }) => Promise<Statement>;
  recompute: (invoiceId: string, idempotencyKey: string) => Promise<CommissionPreview[]>;
}

/** Build a query string, leaving absent filters out entirely. */
export function queryString(query: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') {
      search.set(key, value);
    }
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

/**
 * The path of R20's thirteen-column CSV.
 *
 * A path and not a `fetch`: the browser's own download, with the session cookie, is what
 * puts a file on the accountant's disk with the api's `Content-Disposition` filename. The
 * same reason BP-02's purchase-order PDF is opened rather than parsed.
 */
export function statementCsvPath(query: { period: string; staff_id: string }): string {
  return `/api/v1/commissions/statements.csv${queryString(query)}`;
}

/** The public rating link's path (R13) — no session, the token is the whole credential. */
export function ratingPath(token: string): string {
  return `/v1/public/ratings/${encodeURIComponent(token)}`;
}

/** Bind the §5 session and commission routes to a transport. */
export function createSessionsApi(fetcher: Fetcher): SessionsApi {
  const idem = (key: string): Record<string, string> => ({
    'Idempotency-Key': key,
  });
  const detail = (path: string, spec: Parameters<Fetcher['json']>[1]): Promise<SessionDetail> =>
    fetcher.json(path, spec, sessionDetailSchema);
  return {
    readTime: () => fetcher.json('/v1/time', { method: 'GET' }, timeSchema),
    listSessions: (query = {}) =>
      fetcher.json(
        `/v1/sessions${queryString({ ...query })}`,
        { method: 'GET' },
        sessionPageSchema,
      ),
    readSession: (id) => detail(`/v1/sessions/${id}`, { method: 'GET' }),
    createSession: (body, key) =>
      detail('/v1/sessions', { method: 'POST', body, headers: idem(key) }),
    startStep: (id, body, key) =>
      detail(`/v1/sessions/${id}/steps`, {
        method: 'POST',
        body,
        headers: idem(key),
      }),
    stopStep: (id, key) =>
      detail(`/v1/sessions/${id}/steps/current/stop`, {
        method: 'POST',
        headers: idem(key),
      }),
    pause: (id, key) =>
      detail(`/v1/sessions/${id}/pause`, {
        method: 'POST',
        headers: idem(key),
      }),
    resume: (id, key) =>
      detail(`/v1/sessions/${id}/resume`, {
        method: 'POST',
        headers: idem(key),
      }),
    handover: (id, body, key) =>
      detail(`/v1/sessions/${id}/handover`, {
        method: 'POST',
        body,
        headers: idem(key),
      }),
    addMaterial: (id, body, key) =>
      detail(`/v1/sessions/${id}/materials`, {
        method: 'POST',
        body,
        headers: idem(key),
      }),
    changeMaterial: (id, materialId, body) =>
      detail(`/v1/sessions/${id}/materials/${materialId}`, {
        method: 'PATCH',
        body,
      }),
    removeMaterial: (id, materialId) =>
      detail(`/v1/sessions/${id}/materials/${materialId}`, {
        method: 'DELETE',
      }),
    repeatLast: (id, key) =>
      detail(`/v1/sessions/${id}/repeat-last`, {
        method: 'POST',
        headers: idem(key),
      }),
    addNote: (id, body, key) =>
      detail(`/v1/sessions/${id}/notes`, {
        method: 'POST',
        body,
        headers: idem(key),
      }),
    readClientProfile: (id) =>
      fetcher.json(`/v1/sessions/${id}/client-profile`, { method: 'GET' }, clientProfileSchema),
    complete: (id, confirmMaterials, key) =>
      detail(`/v1/sessions/${id}/complete`, {
        method: 'POST',
        body: { confirm_materials: confirmMaterials },
        headers: idem(key),
      }),
    abandon: (id, body, key) =>
      detail(`/v1/sessions/${id}/abandon`, {
        method: 'POST',
        body,
        headers: idem(key),
      }),
    readEvents: (id) =>
      fetcher.json(`/v1/sessions/${id}/events`, { method: 'GET' }, z.array(eventSchema)),
    listRules: () =>
      fetcher.json('/v1/commission-rules', { method: 'GET' }, z.array(commissionRuleSchema)),
    createRule: (body, key) =>
      fetcher.json(
        '/v1/commission-rules',
        { method: 'POST', body, headers: idem(key) },
        commissionRuleSchema,
      ),
    updateRule: (id, body) =>
      fetcher.json(`/v1/commission-rules/${id}`, { method: 'PATCH', body }, commissionRuleSchema),
    previewRules: (invoiceId) =>
      fetcher.json(
        '/v1/commission-rules/preview',
        { method: 'POST', body: { invoice_id: invoiceId } },
        z.array(commissionPreviewSchema),
      ),
    readStatement: (query) =>
      fetcher.json(
        `/v1/commissions/statements${queryString({ ...query })}`,
        { method: 'GET' },
        statementSchema,
      ),
    recompute: (invoiceId, key) =>
      fetcher.json(
        '/v1/commissions/recompute',
        { method: 'POST', body: { invoice_id: invoiceId }, headers: idem(key) },
        z.array(commissionPreviewSchema),
      ),
  };
}

/** `POST /public/ratings/{token}` — the client app's one call, no session (R13). */
export function createRatingApi(fetcher: Fetcher): {
  rate: (token: string, score: number, text: string | null) => Promise<void>;
} {
  return {
    rate: (token, score, text) =>
      fetcher.empty(ratingPath(token), {
        method: 'POST',
        body: { score, text },
      }),
  };
}
